import { Component, DestroyRef, inject, linkedSignal, OnInit, Renderer2, signal, DOCUMENT } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { UiCoreEllipsisLoaderComponent } from "@rr/shared-ui-core";
import { ApiStore, AuthStore, PosthogService, ThemeService } from "@rr/shared-data-access-core";
import { debounceTime, distinctUntilChanged, filter, take } from "rxjs";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Toast } from "primeng/toast";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ErrorModalComponent } from "@rr/feature-exception-view-error-modal";
import { DataPrivacyComponent } from "@rr/feature-system-notice";
@Component({
  imports: [
    RouterOutlet,
    ConfirmDialog,
    Toast,
    UiCoreEllipsisLoaderComponent,
    ErrorModalComponent,
    DataPrivacyComponent,
  ],
  selector: "rr-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit {
  // DI
  readonly #titleService = inject(Title);
  readonly #authService = inject(AuthService);
  readonly #themeService = inject(ThemeService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #renderer = inject(Renderer2);
  readonly #document = inject(DOCUMENT);
  readonly #router = inject(Router);
  authStore = inject(AuthStore);
  apiStore = inject(ApiStore);

  // Template variables
  loading = signal(true);
  showDataPrivacy = linkedSignal(() => !!this.authStore.theme() && !!this.authStore.primaryOrganization());

  constructor() {
    inject(PosthogService);
  }

  ngOnInit(): void {
    this.processLoader();
    this.processAuthCallback();
    this.setTitle();
    this.setPresets();
  }

  private setPresets() {
    const mode = this.authStore.theme()?.mode ?? "rr-theme";

    this.#renderer.addClass(this.#document.documentElement, mode);
    this.#renderer.setAttribute(
      this.#document.documentElement,
      "data-bs-theme",
      mode.includes("dark") ? "dark" : "light",
    );
    this.#themeService.updatePreset(this.authStore.theme()?.style?.colors ?? null, mode);
  }

  private setTitle() {
    const org = this.authStore.user() ? this.authStore.user()!["agOrgName"] : null;
    if (org) {
      this.#titleService.setTitle(`${org} - ${this.#titleService.getTitle()}`);
    }
  }

  private processLoader() {
    this.#router.events
      .pipe(
        // Debounce initial auth callback by 1500ms to display loader/logo for branding after login
        debounceTime(this.authStore.callback() ? 1500 : 0),
        distinctUntilChanged(),
        filter((e) => e instanceof NavigationCancel || e instanceof NavigationError || e instanceof NavigationEnd),
        take(1),
      )
      .subscribe({
        next: () => this.loading.set(false),
      });
  }

  private processAuthCallback() {
    const authCallback = this.authStore.callback();
    this.#authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        if (authCallback) {
          authCallback();
        }
      });
  }
}
